import React from "react";
import { Link, useLocation } from "react-router-dom";
import "./Sidebar.css";
import { useTenant } from "../contexts/TenantContext";

const PERMISSIONS = {
  super_admin: { dashboard:true,sales:true,clients:true,channel_partners:true,media:true,walkins:true,followups:true,vdnb:true,inventory:true,sales_strategy:true,launch_war_room:true,collaterals:true,cost_sheet:true,broker_marketing:true,saledrive_meta:true,automated_caller:true,pixeldo_drone:true,whatsapp_intimation:true,reports:true,admin:true,construction_milestones:true,microsite_domains:true,social_media:true,intelligence:true,access_control:true },
  admin: { dashboard:true,sales:true,clients:true,channel_partners:true,media:true,walkins:true,followups:true,vdnb:true,inventory:true,sales_strategy:true,launch_war_room:true,collaterals:true,cost_sheet:true,broker_marketing:true,saledrive_meta:true,automated_caller:true,pixeldo_drone:true,whatsapp_intimation:true,reports:true,admin:true,construction_milestones:true,microsite_domains:true,social_media:true,intelligence:true,access_control:true },
  director: { dashboard:true,sales:true,clients:true,channel_partners:true,media:true,walkins:true,followups:true,vdnb:true,inventory:true,sales_strategy:true,launch_war_room:true,collaterals:true,cost_sheet:true,broker_marketing:true,saledrive_meta:true,automated_caller:true,pixeldo_drone:true,whatsapp_intimation:true,reports:true,admin:true,construction_milestones:true,microsite_domains:true,social_media:true,intelligence:true,access_control:true },
  sales_head: { dashboard:true,sales:true,clients:true,channel_partners:true,media:true,walkins:true,followups:true,inventory:true,sales_strategy:true,launch_war_room:true,collaterals:true,cost_sheet:true,reports:true,construction_milestones:true,microsite_domains:true,social_media:true,intelligence:true },
  sales: { dashboard:true,clients:true,inventory:true,walkins:true,followups:true,cost_sheet:true,channel_partners:true },
  sales_coordinator: { dashboard:true,clients:true,inventory:true,walkins:true,followups:true,collaterals:true,cost_sheet:true },
  sourcing: { dashboard:true,channel_partners:true },
  receptionist: { dashboard:true,walkins:true,clients:true }
};

const Sidebar = ({ user, onLogout, isOpen, onClose, collapsed, onToggleCollapse }) => {
  const { tenant } = useTenant();
  const location = useLocation();
  const userRole = user?.role || "sales";
  
  const checkAccess = (module) => {
    if (userRole === "admin" || userRole === "super_admin" || userRole === "superadmin") return true;
    if (user?.permissions && Array.isArray(user.permissions)) {
      const override = user.permissions.find(p => p.module === module);
      if (override) return override.can_read === 1;
    }
    const rolePerms = PERMISSIONS[userRole] || PERMISSIONS.sales;
    return rolePerms[module] || rolePerms.admin;
  };

  const handleNavClick = () => {
    if (window.innerWidth <= 1024 && onClose) onClose();
  };

  const allMenuItems = [
    { path:"/",label:"Dashboard",icon:"📊",permission:"dashboard" },
    { path:"/intelligence",label:"The Intelligence",icon:"🧠",permission:"intelligence" },
    { path:"/master-data",label:"Master Data",icon:"🏛️",permission:"admin" },
    { path:"/projects",label:"Projects",icon:"🏗️",permission:"admin" },
    { path:"/clients",label:"Clients",icon:"👥",permission:"clients" },
    { path:"/sales",label:"Sales",icon:"💰",permission:"sales" },
    { path:"/sales-strategy",label:"Sales Strategy",icon:"🎯",permission:"sales_strategy" },
    { path:"/launch-war-room",label:"Launch War Room",icon:"🚀",permission:"launch_war_room" },
    { path:"/channel-partners",label:"Channel Partners",icon:"🤝",permission:"channel_partners" },
    { path:"/walkins",label:"Walk-ins",icon:"🚶",permission:"walkins" },
    { path:"/followups",label:"Follow-ups",icon:"📞",permission:"followups" },
    { path:"/booking-board",label:"Booking board",icon:"🏗️",permission:"inventory" },
    { path:"/inventory",label:"Inventory",icon:"🏢",permission:"inventory" },
    { path:"/cost-sheet",label:"Cost Sheet",icon:"📄",permission:"cost_sheet" },
    { path:"/npv-calculator",label:"NPV Calculator",icon:"🧮",permission:"sales" },
    { path:"/eoi",label:"EOI",icon:"📝",permission:"cost_sheet" },
    { path:"/overrides",label:"Superadmin override",icon:"🔓",permission:"admin",superOnly:true },
    { path:"/collaterals",label:"Collaterals",icon:"📎",permission:"collaterals" },
    { path:"/media",label:"Media",icon:"🖼️",permission:"media" },
    { path:"/vdnb",label:"VDNB (Sell)",icon:"🏷️",permission:"vdnb" },
    { path:"/broker-marketing",label:"The Real Connect",icon:"📢",permission:"broker_marketing" },
    { path:"/whatsapp-templates",label:"WhatsApp Templates",icon:"🗂️",permission:"whatsapp_intimation" },
    { path:"/social-media",label:"Social Media",icon:"📱",permission:"social_media" },
    { path:"/microsite-domains",label:"Microsites",icon:"🌐",permission:"microsite_domains" },
    { path:"/chatbot",label:"AI Assistant",icon:"🤖",permission:"dashboard" },
    { path:"/construction-milestones",label:"Construction",icon:"🏗️",permission:"construction_milestones" },
    { path:"/reports",label:"Reports",icon:"📈",permission:"reports" },
    { path:"/activity-report",label:"Team Activity",icon:"📋",permission:"reports" },
    { path:"/access-control",label:"Access Control",icon:"🔐",permission:"access_control" },
    { path:"/admin",label:"Admin",icon:"⚙️",permission:"admin" },
  ];

  // TEMPORARILY HIDDEN (owner, 2026-09-10). To restore, empty this array and
  // rebuild the client - nothing else was removed.
  const HIDDEN_PATHS = ['/sales-strategy', '/launch-war-room'];

  const menuItems = allMenuItems
    .filter(item => !HIDDEN_PATHS.includes(item.path))
    .filter(item => !item.superOnly || userRole === "superadmin" || userRole === "super_admin")
    .filter(item => checkAccess(item.permission));

  return (
    <div className={`sidebar ${isOpen ? "open" : ""} ${collapsed ? "collapsed" : ""}`}>
      <div className="sidebar-header">
        <h2>{collapsed ? tenant.name?.charAt(0) : tenant.name}</h2>
        {!collapsed && <p>Kohinoor @ Patel Estate</p>}
        <button className="collapse-toggle" onClick={onToggleCollapse} title={collapsed ? "Expand" : "Collapse"}>
          {collapsed ? "»" : "«"}
        </button>
      </div>
      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          <Link key={item.path} to={item.path}
            className={`nav-item ${location.pathname === item.path ? "active" : ""}`}
            onClick={handleNavClick} title={collapsed ? item.label : ""}>
            <span className="nav-icon">{item.icon}</span>
            {!collapsed && <span className="nav-label">{item.label}</span>}
          </Link>
        ))}
      </nav>
      <div className="sidebar-footer">
        {!collapsed && (
          <div className="user-info">
            <div className="user-avatar">{user?.name?.charAt(0).toUpperCase()}</div>
            <div className="user-details">
              <div className="user-name">{user?.name}</div>
              <div className="user-role">{user?.role}</div>
            </div>
          </div>
        )}
        <button onClick={onLogout} className="logout-btn">
          {collapsed ? "🚪" : "Logout"}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
